import { useState } from 'react';
import type { JournalEntry, JournalKind, JournalStream, JournalSummary } from '@life-os/app-core';
import { STREAM_KINDS } from '@life-os/app-core';
import { Button, Chip, EmptyState, Pane, Stack } from '@life-os/ui';
import { useServices, useStore } from '../app/store.js';
import { useAsync } from '../lib/useAsync.js';
import { ScreenHeader } from '../app/ScreenHeader.js';

const inputStyle = {
  background: 'var(--lc-surface-container)',
  color: 'var(--lc-on-surface)',
  border: '1px solid var(--lc-outline-variant)',
  borderRadius: 'var(--lc-radius-full)',
  padding: '0 12px',
  height: 34,
  font: 'inherit',
  fontSize: 'var(--lc-text-sm)',
} as const;

function weekRange(): { from: string; to: string } {
  const now = new Date();
  const from = new Date(now);
  from.setUTCDate(from.getUTCDate() - 6);
  return { from: from.toISOString().slice(0, 10), to: now.toISOString().slice(0, 10) };
}

export interface JournalViewProps {
  readonly stream: JournalStream;
  readonly title: string;
  readonly subtitle: string;
  readonly placeholder: string;
  readonly privacyNote: string;
}

/** Shared reflective-journal screen used by both the Mental and Spiritual streams. */
export function JournalView({ stream, title, subtitle, placeholder, privacyNote }: JournalViewProps) {
  const services = useServices();
  const { revision, refresh } = useStore();
  const kinds = STREAM_KINDS[stream];
  const [entryKind, setEntryKind] = useState<JournalKind>(kinds[0]!);
  const [body, setBody] = useState('');

  const entries = useAsync<readonly JournalEntry[]>(() => services.journal.list(stream), [revision, stream]);
  const { from, to } = weekRange();
  const summary = useAsync<JournalSummary>(() => services.journal.summary(stream, from, to), [revision, stream]);

  const add = async () => {
    if (body.trim().length === 0) return;
    await services.journal.add({ stream, entryKind, body });
    setBody('');
    refresh();
  };

  const recent = [...(entries.value ?? [])].sort((a, b) => b.occurredOn.localeCompare(a.occurredOn)).slice(0, 30);
  const s = summary.value;

  return (
    <>
      <ScreenHeader title={<>{title}</>} subtitle={<>{subtitle}</>} actions={<><Chip tone={stream === 'mental' ? 'warn' : 'neutral'}>{stream === 'mental' ? 'strictly local' : 'private'}</Chip></>} />

      <Pane title="New entry" hint={privacyNote}>
        <Stack>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {kinds.map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setEntryKind(k)}
                aria-pressed={entryKind === k}
                style={{
                  ...inputStyle,
                  height: 30,
                  cursor: 'pointer',
                  background: entryKind === k ? 'var(--lc-primary)' : 'var(--lc-surface-container)',
                  color: entryKind === k ? 'var(--lc-on-primary)' : 'var(--lc-on-surface-variant)',
                }}
              >
                {k}
              </button>
            ))}
          </div>
          <textarea
            className="lc-cmdbar"
            style={{ minHeight: 96, resize: 'vertical' }}
            value={body}
            placeholder={placeholder}
            onChange={(e) => setBody(e.target.value)}
            aria-label="Entry body"
          />
          <div>
            <Button variant="primary" onClick={() => void add()}>
              Save entry
            </Button>
          </div>
        </Stack>
      </Pane>

      {s && s.count > 0 ? (
        <Pane title="This week" hint={`${s.count} ${s.count === 1 ? 'entry' : 'entries'} · ${s.daysJournaled} day${s.daysJournaled === 1 ? '' : 's'}`}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {kinds.map((k) => (s.byKind[k] > 0 ? <Chip key={k} tone="neutral">{`${k} ${s.byKind[k]}`}</Chip> : null))}
          </div>
        </Pane>
      ) : null}

      <Pane title="Entries" hint={`${recent.length}`}>
        <Stack>
          {recent.length === 0 ? (
            <EmptyState title="Nothing here yet">This space is yours. Write freely — it stays private on your device.</EmptyState>
          ) : (
            recent.map((e) => (
              <div key={String(e.id)} className="lc-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Chip tone="muted">{e.entryKind}</Chip>
                  <span style={{ color: 'var(--lc-on-surface-variant)', fontSize: 'var(--lc-text-sm)' }}>{e.occurredOn.slice(0, 10)}</span>
                </div>
                <div style={{ whiteSpace: 'pre-wrap' }}>{e.body}</div>
              </div>
            ))
          )}
        </Stack>
      </Pane>
    </>
  );
}
